// Third Party
import * as React from 'react';
import { Transition } from '@headlessui/react';
import assertNever from 'assert-never';
import cx from 'classnames';

// Utils
import useOnclickOutside from 'react-cool-onclickoutside';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleX,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';

const useMounted = () => {
  const isMounted = React.useRef(false);

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};

/**
 * useAsyncSafeSetTimeout is a safe wrapper around setTimeout to prevent memory leaks. It will only execute the callback if the component is still mountd, and it automatically clears the timer on unmount.
 *
 */
function useAsyncSafeSetTimeout() {
  const isMounted = useMounted();
  const timeoutId = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  return (callback: () => void, delay: number) => {
    const checkMountBeforeExecution = () => {
      isMounted.current && callback();
    };
    timeoutId.current = window.setTimeout(checkMountBeforeExecution, delay);
  };
}

export type NotificationType = 'error' | 'success' | 'warning';

export const NotificationCard: React.FC<{
  markForRemoval?: boolean;
  uniqueId: string;
  message?: string;
  timeout?: number;
  title: string;
  type: NotificationType;
  removeNotification: (notificationId: string) => void;
}> = ({
  uniqueId,
  markForRemoval,
  timeout,
  type,
  title,
  message,
  removeNotification,
}) => {
  const asyncSafeSetTimeout = useAsyncSafeSetTimeout();
  const [show, setShow] = React.useState(true);
  const onClickOutsideRef = useOnclickOutside(() =>
    removeNotification(uniqueId)
  );
  // Begin fade-out and remove when marked for removal
  React.useEffect(() => {
    if (markForRemoval) setShow(false);
  }, [markForRemoval]);

  React.useEffect(() => {
    if (timeout) {
      asyncSafeSetTimeout(() => setShow(false), timeout);
    }
  }, []);
  return (
    <Transition
      appear
      show={show}
      as="div"
      className="w-full max-w-sm flex justify-center items-center md:justify-end"
      enter="transition-all ease-out duration-500 transform"
      enterFrom="opacity-0 translate-x-full"
      enterTo="opacity-100 translate-x-0"
      leave="transition-all duration-500 transform"
      leaveTo="opacity-0"
    >
      <div
        ref={onClickOutsideRef}
        className={cx(
          'ring-1 ring-black ring-opacity-5 border-1 w-full max-w-sm bg-white rounded shadow-xl pointer-events-auto overflow-hidden',
          {
            'border-success-main': type === 'success',
            'border-alert-main': type === 'warning',
            'border-error-main': type === 'error',
          }
        )}
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {(() => {
                switch (type) {
                  case 'success':
                    return (
                      <FontAwesomeIcon
                        data-id={`ICON-faCircleCheck`}
                        icon={faCircleCheck}
                        className={'icon text-success-main'}
                      />
                    );
                  case 'warning':
                    return (
                      <FontAwesomeIcon
                        data-id={`ICON-faTriangleExclamation`}
                        icon={faTriangleExclamation}
                        className={'icon text-alert-main'}
                      />
                    );

                  case 'error':
                    return (
                      <FontAwesomeIcon
                        data-id={`ICON-faCircleExclamation`}
                        icon={faCircleExclamation}
                        className={'icon text-error-main'}
                      />
                    );
                  default:
                    return assertNever(type);
                }
              })()}
            </div>
            <div className="pt-0.5 flex-1 ml-3 w-0">
              <p className="text-gray-dark text-sm font-medium">{title}</p>
              {message && (
                <p className="mt-1 text-gray-main text-sm">{message}</p>
              )}
            </div>
            <div className="flex flex-shrink-0 ml-4">
              <button
                className="inline-flex text-gray-main hover:text-gray-dark bg-white rounded-md"
                onClick={() => setShow(false)}
              >
                <span className="sr-only">Close</span>

                <FontAwesomeIcon
                  data-id={`ICON-faCircleX`}
                  icon={faCircleX}
                  className={'icon'}
                  size="lg"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};
